import styled, { css, keyframes } from "styled-components";

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.8); opacity: 0.4; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

export const Panel = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.space[3]};
  left: ${({ theme }) => theme.space[4]};
  bottom: calc(${({ theme }) => theme.space[10]} + 52px);
  width: 340px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.radius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  display: flex;
  flex-direction: column;
  z-index: ${({ theme }) => theme.zIndex.overlay};
  overflow: hidden;

  @media screen and (max-width: 480px) {
    left: ${({ theme }) => theme.space[2]};
    right: ${({ theme }) => theme.space[2]};
    width: auto;
    top: ${({ theme }) => theme.space[2]};
    bottom: calc(${({ theme }) => theme.space[8]} + 52px);
  }
`;

export const PanelHeader = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.space[4]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[3]};
  flex-shrink: 0;
`;

export const BusIconHeader = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

export const HeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const HeaderTitle = styled.div`
  font-size: ${({ theme }) => theme.fonts.base};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const HeaderSubtitle = styled.div`
  font-size: ${({ theme }) => theme.fonts.xs};
  color: ${({ theme }) => theme.colors.white};
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

export const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: ${({ theme }) => theme.colors.white};
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.radius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  flex-shrink: 0;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

export const StopsSection = styled.div`
  padding: ${({ theme }) => theme.space[3]} ${({ theme }) => theme.space[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  flex-shrink: 0;
`;

export const StopsSectionTitle = styled.div`
  font-size: ${({ theme }) => theme.fonts.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray700};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const StopsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.space[3]} ${({ theme }) => theme.space[4]};

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.gray300};
    border-radius: 2px;
  }
`;

export const StopItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[3]};
  min-height: 64px;

  ${({ state, theme }) =>
    state === "next" &&
    css`
      background: ${theme.colors.primaryBg};
      border-radius: ${theme.radius.md};
      margin: 0 -${theme.space[2]};
      padding: ${theme.space[2]};
    `}

  ${({ state, theme }) =>
    state === "skipped" &&
    css`
      opacity: 0.7;
    `}
`;

export const StopTimeline = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 24px;
  padding-top: ${({ theme }) => theme.space[3]};
`;

export const StopDotWrapper = styled.div`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

export const StopDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;

  ${({ state, theme }) => {
    switch (state) {
      case "passed":
      case "lastPassed":
        return css`
          background: ${theme.colors.success};
          position: relative;

          &::after {
            content: "";
            position: absolute;
            top: 3px;
            left: 3.5px;
            width: 4px;
            height: 6px;
            border: solid ${theme.colors.white};
            border-width: 0 1.5px 1.5px 0;
            transform: rotate(45deg);
          }
        `;
      case "next":
        return css`
          background: ${theme.colors.white};
          border: 2.5px solid ${theme.colors.primary};
          box-sizing: border-box;
        `;
      case "skipped":
        return css`
          background: ${theme.colors.warning};
          position: relative;

          &::after {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            width: 8px;
            height: 2px;
            background: ${theme.colors.white};
            transform: translate(-50%, -50%);
            border-radius: 1px;
          }
        `;
      case "lastStop":
        return css`
          background: ${theme.colors.primary};
        `;
      case "future":
      default:
        return css`
          background: ${theme.colors.gray300};
        `;
    }
  }}
`;

export const PulseRing = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  margin-top: -14px;
  animation: ${pulse} 2s ease-out infinite;
  pointer-events: none;
`;

export const StopLine = styled.div`
  width: 2.5px;
  flex: 1;
  min-height: 20px;
  margin: 4px 0;

  ${({ state, theme }) => {
    switch (state) {
      case "passed":
        return css`
          background: ${theme.colors.success};
        `;
      case "lastPassed":
        return css`
          background: linear-gradient(
            to bottom,
            ${theme.colors.success} 0%,
            transparent 100%
          );
          position: relative;

          &::after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: ${theme.colors.success};
            animation: ${pulseDot} 1.5s ease-in-out infinite;
          }
        `;
      case "skipped":
        return css`
          background: ${theme.colors.warning};
          opacity: 0.4;
        `;
      case "future":
      case "next":
      default:
        return css`
          background-image: repeating-linear-gradient(
            to bottom,
            ${theme.colors.gray300} 0px,
            ${theme.colors.gray300} 4px,
            transparent 4px,
            transparent 8px
          );
        `;
    }
  }}
`;

export const StopContent = styled.div`
  flex: 1;
  min-width: 0;
  padding-bottom: ${({ theme }) => theme.space[3]};
`;

export const StopName = styled.div`
  font-size: ${({ theme }) => theme.fonts.sm};
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ state, theme }) => {
    switch (state) {
      case "passed":
      case "lastPassed":
        return css`
          color: ${theme.colors.gray500};
        `;
      case "next":
        return css`
          color: ${theme.colors.primary};
          font-weight: 700;
        `;
      case "skipped":
        return css`
          color: ${theme.colors.gray400};
          text-decoration: line-through;
        `;
      default:
        return css`
          color: ${theme.colors.gray700};
        `;
    }
  }}
`;

export const StopTime = styled.div`
  font-size: ${({ theme }) => theme.fonts.xs};
  color: ${({ theme }) => theme.colors.gray500};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[1]};
  flex-wrap: wrap;
  margin-top: 2px;
`;

export const TimeStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;

  ${({ status, theme }) => {
    switch (status) {
      case "ontime":
        return css`
          background: ${theme.colors.successLighter};
          color: ${theme.colors.success};
        `;
      case "late":
        return css`
          background: ${theme.colors.errorLight};
          color: ${theme.colors.error};
        `;
      case "skipped":
        return css`
          background: #fff3cd;
          color: #856404;
        `;
      case "early":
        return css`
          background: ${theme.colors.primaryBg};
          color: ${theme.colors.primary};
        `;
      default:
        return css``;
    }
  }}
`;

export const StopAddress = styled.div`
  font-size: ${({ theme }) => theme.fonts.xs};
  color: ${({ theme }) => theme.colors.gray400};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;
